import React, { useState } from "react";
import { Cpu, Hash, ShieldCheck, Lock, FileSignature, Info } from "lucide-react";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
    DialogFooter,
} from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { toast } from "sonner";
import { signAudit } from "../lib/api";

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleString() : "—");
const short = (h, n = 12) => (h ? `${h.slice(0, n)}…` : "—");

export const AuditProvenanceCard = ({ audit, onSigned }) => {
    if (!audit || !audit.id) return null;

    return (
        <div className="mx-8 md:mx-12 mt-8 border border-hair bg-white">
            {/* Summary */}
            <div className="p-6 border-b border-hair">
                <div className="flex items-center justify-between flex-wrap gap-3">
                    <div className="overline flex items-center gap-2">
                        <Info size={12} /> AudRI Summary
                    </div>
                    <ReviewerBadge audit={audit} />
                </div>
                <p className="text-sm leading-relaxed mt-3">
                    {audit.summary || "No narrative summary available."}
                </p>
            </div>

            {/* Provenance grid — model, prompt version, temperature, seed, timestamp */}
            <div className="grid grid-cols-2 md:grid-cols-6 border-b border-hair">
                <Meta
                    icon={Cpu}
                    label="Model"
                    value={audit.model || "—"}
                    sub={audit.model_provider || "—"}
                    testid="prov-model"
                />
                <Meta
                    label="Prompt version"
                    value={audit.prompt_version || "—"}
                    sub="Versioned prompt template"
                    testid="prov-prompt"
                />
                <Meta
                    label="Temperature"
                    value={
                        audit.temperature != null
                            ? audit.temperature.toFixed(2)
                            : "—"
                    }
                    sub="Determinism parameter"
                    testid="prov-temp"
                />
                <Meta
                    label="Seed"
                    value={audit.seed != null ? String(audit.seed) : "—"}
                    sub="Replay seed"
                    testid="prov-seed"
                />
                <Meta
                    label="Analyzed at"
                    value={fmtDate(audit.completed_at)}
                    sub={
                        audit.duration_ms
                            ? `${(audit.duration_ms / 1000).toFixed(1)}s runtime`
                            : "—"
                    }
                    testid="prov-time"
                />
                <Meta
                    label="Avg confidence"
                    value={
                        audit.avg_confidence != null
                            ? `${audit.avg_confidence}%`
                            : "—"
                    }
                    sub={
                        audit.low_confidence_items != null
                            ? `${audit.low_confidence_items} items <70%`
                            : "—"
                    }
                    testid="prov-conf"
                    accent={
                        audit.avg_confidence != null && audit.avg_confidence >= 80
                    }
                    warn={
                        audit.avg_confidence != null && audit.avg_confidence < 70
                    }
                />
            </div>

            {/* Input document integrity */}
            <div className="p-6 border-b border-hair">
                <div className="overline mb-3 flex items-center gap-2">
                    <Hash size={12} /> Input integrity · SHA-256
                </div>
                {(audit.input_documents || []).length === 0 ? (
                    <div className="text-sm text-neutral-500">
                        No input documents recorded for this audit.
                    </div>
                ) : (
                    <div className="space-y-1">
                        {audit.input_documents.map((d) => (
                            <div
                                key={d.filename + d.sha256}
                                className="grid grid-cols-12 gap-3 text-[12px] items-center"
                                data-testid={`input-${d.filename}`}
                            >
                                <div className="col-span-2">
                                    <span className="mono text-[10px] px-2 py-[1px] border border-hair rounded-sm">
                                        {d.doc_type}
                                    </span>
                                </div>
                                <div className="col-span-4 truncate">
                                    {d.filename}
                                </div>
                                <div className="col-span-1 mono text-neutral-500">
                                    rev {d.revision}
                                </div>
                                <div className="col-span-4 mono text-neutral-500 truncate">
                                    {short(d.sha256, 32)}
                                </div>
                                <div className="col-span-1 text-right mono text-neutral-500">
                                    {d.size}B
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* E-signature */}
            <ESignaturePanel audit={audit} onSigned={onSigned} />
        </div>
    );
};

const Meta = ({ icon: Icon, label, value, sub, testid, accent, warn }) => (
    <div
        className="p-5 border-r border-hair last:border-r-0"
        data-testid={testid}
    >
        <div className="overline flex items-center gap-1">
            {Icon && <Icon size={10} />} {label}
        </div>
        <div
            className={`heading font-medium tracking-tight mt-2 text-lg truncate ${
                accent
                    ? "text-[#064E3B]"
                    : warn
                      ? "text-[#991B1B]"
                      : ""
            }`}
        >
            {value}
        </div>
        <div className="text-[10px] text-neutral-500 mt-1 mono truncate">
            {sub}
        </div>
    </div>
);

const ReviewerBadge = ({ audit }) => {
    const status = audit.reviewer_status || "Draft";
    const cls =
        status === "Approved"
            ? "verdict-pass"
            : status === "In Review"
              ? "verdict-conditional"
              : "verdict-missing";
    return (
        <span
            data-testid="reviewer-status"
            className={`mono inline-flex items-center gap-1 px-2 py-[3px] text-[11px] tracking-wider border rounded-sm ${cls}`}
        >
            <ShieldCheck size={11} />
            {status}
        </span>
    );
};

const ESignaturePanel = ({ audit, onSigned }) => {
    const sig = audit.e_signature;
    return (
        <div className="p-6">
            <div className="overline mb-3 flex items-center gap-2">
                <FileSignature size={12} /> 21 CFR Part 11 · Electronic Signature
            </div>
            {sig ? (
                <div className="border border-hair bg-[#fafafa] p-4 grid grid-cols-1 md:grid-cols-4 gap-4">
                    <Kv label="Signer" value={sig.signer_name} />
                    <Kv label="Role" value={sig.signer_role} />
                    <Kv label="Meaning" value={sig.meaning} />
                    <Kv label="Signed at" value={fmtDate(sig.signed_at)} />
                    <div className="md:col-span-4 text-[11px] mono text-neutral-500 truncate">
                        content_hash: {sig.content_hash}
                    </div>
                </div>
            ) : (
                <div className="flex items-center justify-between gap-3 flex-wrap">
                    <p className="text-sm text-neutral-600">
                        Apply a Part 11–compliant e-signature to formally approve
                        this periodic review. Signature includes signer, role,
                        meaning, timestamp and a SHA-256 content hash.
                    </p>
                    <SignDialog audit={audit} onSigned={onSigned} />
                </div>
            )}
        </div>
    );
};

const Kv = ({ label, value }) => (
    <div>
        <div className="overline">{label}</div>
        <div className="text-sm heading font-medium mt-1 truncate">{value}</div>
    </div>
);

const SignDialog = ({ audit, onSigned }) => {
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState({
        signer_name: "Sarah Ramirez",
        signer_role: "QA Director",
        meaning: "Reviewed and approved",
        password: "",
    });
    const [busy, setBusy] = useState(false);

    const submit = async () => {
        if (!form.password) {
            toast.error("Password required (21 CFR Part 11 §11.200)");
            return;
        }
        setBusy(true);
        try {
            await signAudit(audit.id, form);
            toast.success("Audit e-signed and approved");
            setOpen(false);
            onSigned?.();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Sign failed");
        } finally {
            setBusy(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
                <button
                    data-testid="btn-sign-audit"
                    className="inline-flex items-center gap-2 bg-[#0a0a0a] text-white text-sm px-4 py-2 rounded-sm hover:bg-[#262626]"
                >
                    <Lock size={13} /> Sign & approve
                </button>
            </DialogTrigger>
            <DialogContent>
                <DialogHeader>
                    <DialogTitle className="heading tracking-tight">
                        Electronic signature · 21 CFR Part 11
                    </DialogTitle>
                </DialogHeader>
                <div className="grid grid-cols-2 gap-4 pt-2">
                    {[
                        ["signer_name", "Signer name", 2],
                        ["signer_role", "Role", 1],
                        ["meaning", "Meaning of signature", 1],
                    ].map(([k, label, span]) => (
                        <div
                            key={k}
                            className={span === 2 ? "col-span-2" : "col-span-1"}
                        >
                            <Label className="text-xs overline">{label}</Label>
                            <Input
                                className="mt-1"
                                value={form[k]}
                                onChange={(e) =>
                                    setForm({ ...form, [k]: e.target.value })
                                }
                                data-testid={`sig-${k}`}
                            />
                        </div>
                    ))}
                    <div className="col-span-2">
                        <Label className="text-xs overline">Password</Label>
                        <Input
                            type="password"
                            className="mt-1"
                            placeholder="Re-enter your login password"
                            value={form.password}
                            onChange={(e) =>
                                setForm({ ...form, password: e.target.value })
                            }
                            data-testid="sig-password"
                        />
                        <div className="text-[11px] text-neutral-500 mt-2 mono">
                            Password is SHA-256 hashed — never stored in
                            plaintext. Required under §11.200(a)(1)(ii).
                        </div>
                    </div>
                </div>
                <DialogFooter>
                    <button
                        data-testid="btn-sign-submit"
                        disabled={busy}
                        onClick={submit}
                        className="inline-flex items-center gap-2 bg-[#C2410C] text-white text-sm px-4 py-2 rounded-sm disabled:opacity-50"
                    >
                        <Lock size={13} /> Apply signature
                    </button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

export const ConfidenceChip = ({ value }) => {
    if (value == null) return null;
    const v = Number(value);
    const cls =
        v >= 80
            ? "verdict-pass"
            : v >= 60
              ? "verdict-conditional"
              : "verdict-fail";
    return (
        <span
            className={`mono text-[10px] uppercase tracking-wider px-2 py-[2px] border rounded-sm ${cls}`}
            title="Model confidence — flagged when <70%"
        >
            {v}%
            {v < 70 && " ⚠"}
        </span>
    );
};
